/**
 * unspun PDP signup popup — client-side logic
 *
 * Phase 3 (slide-in popup) — replaces Phase 1 (inline form) + Phase 2 (A/B test).
 *
 * 2026-05-11 v2: ADDED diagnostic logging (matches cart-tier-nudge.js pattern).
 *   - `window.__unspunLog()` buffer in localStorage (max 200 entries)
 *   - Dump via `__unspunLog.dump()` in dev console
 *   - Logs: init, suppression decisions, trigger fires, popup state changes,
 *     email input focus + keydown + input events, submit attempts with value
 *   - Improved error messages: "Please enter your email address." (empty) vs
 *     "That email doesn't look right." (invalid format) — easier to diagnose
 *
 * 2026-05-11 v3 (BUGFIX): Klaviyo /client/subscriptions/ API spec changed.
 *   `subscriptions: { email: { marketing: { consent } } }` was REMOVED from
 *   profile attributes (returns 400: "'subscriptions' is not a valid field for
 *   the resource 'profile'"). The Subscriptions endpoint inherently records
 *   marketing consent, so the field is now redundant.
 *   Form had been silently failing since 2026-05-06 launch — explains 0 real
 *   signups. Also added: response-body logging for any non-2xx Klaviyo response,
 *   so we catch this class of bug instantly next time.
 *
 * Behaviour unchanged from v1:
 * - 30s timer OR 50% scroll trigger
 * - Suppression: customer (Liquid), cart-not-empty (Liquid), known Klaviyo profile,
 *   previously submitted, previously fully-dismissed
 * - Dismiss popup → teaser pill; dismiss teaser → fully hidden
 * - Submit POSTs to Klaviyo Profiles + Subscriptions endpoints
 *
 * localStorage keys:
 *   - unspun-pdp-slidein-submitted
 *   - unspun-pdp-slidein-dismissed
 *   - unspun-pdp-slidein-teaser-dismissed
 *   - unspun-cart-logs (shared with cart-tier-nudge.js — single global buffer)
 */

(function () {
  'use strict';

  // -----------------------------------------------------------------------
  // Persistent log buffer (shared with cart-tier-nudge.js)
  // -----------------------------------------------------------------------

  if (!window.__unspunLog) {
    var KEY = 'unspun-cart-logs';
    var MAX = 200;
    function getLogs() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } }
    function setLogs(arr) { try { localStorage.setItem(KEY, JSON.stringify(arr.slice(-MAX))); } catch (e) {} }
    window.__unspunLog = function (event, data) {
      var entry = { t: new Date().toISOString(), event: event, data: data || null };
      var logs = getLogs(); logs.push(entry); setLogs(logs);
      console.log('[unspun]', event, data || '');
    };
    window.__unspunLog.dump = function () { console.table(getLogs()); return getLogs(); };
    window.__unspunLog.clear = function () { localStorage.removeItem(KEY); console.log('[unspun] logs cleared'); };
    window.__unspunLog('log buffer initialised — call __unspunLog.dump() to view');
  }

  var log = window.__unspunLog;

  // -----------------------------------------------------------------------
  // Constants
  // -----------------------------------------------------------------------

  var KLAVIYO_COMPANY_ID = 'U3dkRr';
  var KLAVIYO_MASTER_LIST_ID = 'U22MgP';
  var KLAVIYO_SUBSCRIBE_URL =
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_PROFILES_URL =
    'https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_REVISION = '2024-10-15';

  var TRIGGER_TIME_MS = 30000;
  var TRIGGER_SCROLL_PCT = 50;

  var LS_SUBMITTED = 'unspun-pdp-slidein-submitted';
  var LS_DISMISSED = 'unspun-pdp-slidein-dismissed';
  var LS_TEASER_DISMISSED = 'unspun-pdp-slidein-teaser-dismissed';

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function readKlaviyoCookie() {
    var raw = getCookie('__kla_id');
    if (!raw) return null;
    try {
      return JSON.parse(atob(raw));
    } catch (e) {
      return null;
    }
  }

  function isKnownKlaviyoProfile() {
    var c = readKlaviyoCookie();
    return !!(c && c.$exchange_id);
  }

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { localStorage.setItem(key, val); } catch (e) {}
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // -----------------------------------------------------------------------
  // DOM refs
  // -----------------------------------------------------------------------

  var sectionEl, popupEl, teaserEl, backdropEl, closeBtn,
      formEl, emailInput, submitBtn, submitText, submitLoading, errorEl,
      formState, confirmationState, teaserCloseEl,
      codeCopyBtn, codeTextEl;

  function cacheDom() {
    sectionEl         = document.getElementById('pdp-slidein-section');
    popupEl           = document.getElementById('pdp-slidein-popup');
    teaserEl          = document.getElementById('pdp-slidein-teaser');
    backdropEl        = document.getElementById('pdp-slidein-backdrop');
    closeBtn          = document.getElementById('pdp-slidein-close');
    formEl            = document.getElementById('pdp-slidein-form');
    emailInput        = document.getElementById('pdp-slidein-email');
    submitBtn         = document.getElementById('pdp-slidein-submit');
    submitText        = document.querySelector('.pdp-slidein__submit-text');
    submitLoading     = document.querySelector('.pdp-slidein__submit-loading');
    errorEl           = document.getElementById('pdp-slidein-error');
    formState         = document.getElementById('pdp-slidein-form-state');
    confirmationState = document.getElementById('pdp-slidein-confirmation-state');
    teaserCloseEl     = document.getElementById('pdp-slidein-teaser-close');
    codeCopyBtn       = document.getElementById('pdp-slidein-code-copy');
    codeTextEl        = document.getElementById('pdp-slidein-code-text');

    var ok = !!(sectionEl && popupEl && teaserEl);
    log('slidein: cacheDom', {
      ok: ok,
      hasSection: !!sectionEl,
      hasPopup: !!popupEl,
      hasTeaser: !!teaserEl,
      hasForm: !!formEl,
      hasEmail: !!emailInput,
      hasSubmit: !!submitBtn
    });
    return ok;
  }

  // -----------------------------------------------------------------------
  // State management
  // -----------------------------------------------------------------------

  function showPopup() {
    if (!popupEl) return;
    log('slidein: showPopup');
    popupEl.style.display = 'block';
    popupEl.offsetHeight; // reflow
    popupEl.classList.add('is-visible');
    if (backdropEl && window.matchMedia('(max-width: 480px)').matches) {
      backdropEl.classList.add('is-visible');
    }
    if (teaserEl) {
      teaserEl.classList.remove('is-visible');
      setTimeout(function () { teaserEl.style.display = 'none'; }, 400);
    }
  }

  function hidePopupShowTeaser() {
    if (!popupEl) return;
    log('slidein: hidePopupShowTeaser');
    popupEl.classList.remove('is-visible');
    if (backdropEl) backdropEl.classList.remove('is-visible');
    setTimeout(function () { popupEl.style.display = 'none'; }, 600);
    lsSet(LS_DISMISSED, '1');
    showTeaser();
  }

  function showTeaser() {
    if (!teaserEl) return;
    log('slidein: showTeaser');
    teaserEl.style.display = 'flex';
    teaserEl.offsetHeight;
    teaserEl.classList.add('is-visible');
  }

  function hideTeaserFully() {
    if (!teaserEl) return;
    log('slidein: hideTeaserFully');
    teaserEl.classList.remove('is-visible');
    setTimeout(function () { teaserEl.style.display = 'none'; }, 400);
    lsSet(LS_TEASER_DISMISSED, '1');
  }

  function showConfirmation() {
    log('slidein: showConfirmation');
    if (formState) formState.style.display = 'none';
    if (confirmationState) confirmationState.style.display = 'block';
    // Wire up tap-to-copy on the code (idempotent — only wires once)
    if (codeCopyBtn && !codeCopyBtn.__wired) {
      codeCopyBtn.__wired = true;
      codeCopyBtn.addEventListener('click', handleCopyCode);
    }
  }

  function handleCopyCode() {
    if (!codeTextEl || !codeCopyBtn) return;
    var code = codeTextEl.textContent.trim();

    function showCopied() {
      codeCopyBtn.classList.add('is-copied');
      setTimeout(function () {
        codeCopyBtn.classList.remove('is-copied');
      }, 1800);
    }

    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(showCopied).catch(function (err) {
        log('slidein: clipboard API failed, falling back', { error: String(err) });
        fallbackCopy(code, showCopied);
      });
    } else {
      fallbackCopy(code, showCopied);
    }
    log('slidein: code copied', { code: code });
  }

  function fallbackCopy(text, onDone) {
    // execCommand fallback for older browsers / non-HTTPS contexts
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      onDone();
    } catch (e) {
      log('slidein: fallback copy failed', { error: String(e) });
    }
    document.body.removeChild(ta);
  }

  // -----------------------------------------------------------------------
  // Trigger arming
  // -----------------------------------------------------------------------

  function armTriggers() {
    var fired = false;
    function fire(reason) {
      if (fired) return;
      fired = true;
      log('slidein: trigger fired', { reason: reason });
      showPopup();
      window.removeEventListener('scroll', onScroll);
    }

    function onScroll() {
      var scrollTop = window.scrollY || document.documentElement.scrollTop;
      var docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      var pct = (scrollTop / docH) * 100;
      if (pct >= TRIGGER_SCROLL_PCT) fire('scroll-' + Math.round(pct) + 'pct');
    }

    setTimeout(function () { fire('time-' + TRIGGER_TIME_MS + 'ms'); }, TRIGGER_TIME_MS);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // -----------------------------------------------------------------------
  // Submission
  // -----------------------------------------------------------------------

  function setSubmitting(yes) {
    if (!submitBtn) return;
    submitBtn.disabled = !!yes;
    if (submitText && submitLoading) {
      submitText.style.display = yes ? 'none' : 'inline';
      submitLoading.style.display = yes ? 'inline' : 'none';
    }
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.style.display = 'none';
  }

  function handleSubmit(e) {
    e.preventDefault();
    clearError();

    var rawValue = emailInput ? emailInput.value : null;
    var email = (emailInput && emailInput.value || '').trim();

    log('slidein: submit attempted', {
      rawValue: rawValue,
      trimmed: email,
      length: email.length,
      activeElement: document.activeElement ? document.activeElement.id || document.activeElement.tagName : null
    });

    if (!email) {
      log('slidein: submit blocked — empty email');
      showError('Please enter your email address.');
      if (emailInput) emailInput.focus();
      return;
    }
    if (!isValidEmail(email)) {
      log('slidein: submit blocked — invalid format', { email: email });
      showError("That email doesn't look right. Try again?");
      if (emailInput) emailInput.focus();
      return;
    }

    var handle = (sectionEl && sectionEl.getAttribute('data-product-handle')) || 'unknown';
    var signupSource = 'pdp_slidein_' + handle;
    log('slidein: submit proceeding', { email: email, signupSource: signupSource });

    setSubmitting(true);

    var profilePayload = {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: { signup_source: signupSource }
        }
      }
    };

    // Helper: read response body even on non-2xx, log it for diagnostics
    function readResponse(res, label) {
      return res.text().then(function (text) {
        var parsed = null;
        try { parsed = JSON.parse(text); } catch (e) { /* not JSON */ }
        log('slidein: ' + label + ' response', {
          status: res.status,
          ok: res.ok,
          body: parsed || text.slice(0, 500)
        });
        if (!res.ok) {
          var rerr = new Error(label + ' ' + res.status);
          rerr.status = res.status;
          rerr.body = parsed;
          rerr.label = label;
          throw rerr;
        }
        return { res: res, body: parsed, text: text };
      });
    }

    fetch(KLAVIYO_PROFILES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_REVISION
      },
      body: JSON.stringify(profilePayload)
    })
      .then(function (r) { return readResponse(r, 'profile POST'); })
      .then(function () {
        // Klaviyo /client/subscriptions/ payload — 2026-05-11 API SPEC:
        // The `subscriptions` field that used to live in profile.data.attributes
        // is now invalid; this endpoint itself records the marketing consent
        // by virtue of being the subscriptions endpoint.
        var subPayload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: signupSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: KLAVIYO_MASTER_LIST_ID } }
            }
          }
        };
        return fetch(KLAVIYO_SUBSCRIBE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': KLAVIYO_REVISION
          },
          body: JSON.stringify(subPayload)
        });
      })
      .then(function (r) { return readResponse(r, 'subscribe POST'); })
      .then(function (result) {
        lsSet(LS_SUBMITTED, '1');
        showConfirmation();
      })
      .catch(function (err) {
        log('slidein: submit error caught', {
          error: String(err),
          status: err && err.status,
          body: err && err.body,
          label: err && err.label
        });
        console.warn('[pdp-slidein] submit error', err);
        var msg;
        if (err && err.status === 409) {
          // Scenario 1: Klaviyo says already subscribed
          msg = "You're already on the list. Your code should be in your inbox \u2013 email hello@beunspun.co.uk if you can't find it.";
        } else if (
          err && (
            /Failed to fetch/.test(String(err.message || err)) ||
            (typeof err.status === 'number' && err.status >= 400 && err.status < 500)
          )
        ) {
          // Scenario 2: Klaviyo blocked the email (CORS-stripped or 4xx)
          msg = "That address won't go through \u2013 try another, or email hello@beunspun.co.uk.";
        } else {
          // Scenario 3: 5xx, network outage, unknown
          msg = "That didn't go through. Give it a moment and try again.";
        }
        showError(msg);
        setSubmitting(false);
      });
  }

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------

  function init() {
    log('slidein: init start', { url: location.pathname, readyState: document.readyState });

    if (!cacheDom()) {
      log('slidein: init abort — missing DOM elements');
      return;
    }

    var lsSub = lsGet(LS_SUBMITTED);
    var lsTeaserDis = lsGet(LS_TEASER_DISMISSED);
    var knownKlaviyo = isKnownKlaviyoProfile();

    log('slidein: suppression check', {
      submitted: lsSub,
      teaserDismissed: lsTeaserDis,
      knownKlaviyoProfile: knownKlaviyo,
      lsDismissed: lsGet(LS_DISMISSED)
    });

    if (lsSub || lsTeaserDis) {
      log('slidein: suppressed — previously submitted or teaser-dismissed');
      return;
    }

    if (knownKlaviyo) {
      log('slidein: suppressed — known Klaviyo profile');
      return;
    }

    // Wire handlers
    if (closeBtn) closeBtn.addEventListener('click', hidePopupShowTeaser);
    if (formEl) {
      formEl.addEventListener('submit', handleSubmit);
      log('slidein: form submit handler wired');
    } else {
      log('slidein: WARN — no form element found, submit handler NOT wired');
    }

    // Email input diagnostics
    if (emailInput) {
      emailInput.addEventListener('focus', function () {
        log('slidein: email input focused', { value: emailInput.value });
      });
      emailInput.addEventListener('blur', function () {
        log('slidein: email input blurred', { value: emailInput.value });
      });
      emailInput.addEventListener('input', function () {
        log('slidein: email input changed', { value: emailInput.value, length: emailInput.value.length });
      });
      emailInput.addEventListener('keydown', function (e) {
        // Only log first few keys to avoid spam
        if (!emailInput.__keydownCount) emailInput.__keydownCount = 0;
        emailInput.__keydownCount++;
        if (emailInput.__keydownCount <= 5) {
          log('slidein: email keydown', { key: e.key, defaultPrevented: e.defaultPrevented, count: emailInput.__keydownCount });
        }
      });
    }

    if (teaserEl) {
      teaserEl.addEventListener('click', function (e) {
        if (e.target === teaserCloseEl) return;
        showPopup();
      });
    }
    if (teaserCloseEl) {
      teaserCloseEl.addEventListener('click', function (e) {
        e.stopPropagation();
        hideTeaserFully();
      });
      teaserCloseEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          hideTeaserFully();
        }
      });
    }
    if (backdropEl) {
      backdropEl.addEventListener('click', hidePopupShowTeaser);
    }

    if (lsGet(LS_DISMISSED)) {
      log('slidein: previously popup-dismissed → show teaser only');
      showTeaser();
    } else {
      log('slidein: armed (fresh visitor) — 30s OR 50% scroll');
      armTriggers();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

